import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'

import { auth } from 'utils/sdks/authKeycloak'

import { domErrorHandler, errorHandler } from './AuthProviderErrorHandler'

/**
 * AuthContextValues defines the structure for the default values of the {@link AuthContext}.
 */
interface AuthContextValues {
  isAuthenticated: boolean
  login: () => void
  logout: (redirectUri?: string) => void
  isInitialized: boolean
}

/**
 * defaultAuthContextValues defines the default values for the {@link AuthContext}
 */
const defaultAuthContextValues: AuthContextValues = {
  isAuthenticated: false,
  login: () => {},
  logout: () => {},
  isInitialized: false,
}

/**
 * AuthContext is the context exposed by the {@link AuthContextProvider}.
 */
const AuthContext = createContext<AuthContextValues>(defaultAuthContextValues)

export const useAuth = () => {
  const context = useContext(AuthContext)
  return context
}
/**
 * The props that must be passed to create the {@link AuthContextProvider}.
 */

interface AuthProviderProps {
  /**
   * The elements wrapped by the auth context.
   */
  children: JSX.Element
}

/**
 * AuthProvider is responsible for managing the authentication state of the current user.
 *
 * @param props
 */
const AuthProvider = (props: AuthProviderProps) => {
  // @typescript-eslint/no-explicit-any
  const errorMapper: Record<string, (ex: any) => void> = {
    DOMException: domErrorHandler,
  }

  // @typescript-eslint/no-explicit-any
  const getErrorMessage = (error: any) => {
    const error_message = error instanceof Error ? error.message : undefined
    return JSON.stringify(error) ?? error_message ?? error ?? 'undefined error'
  }

  const [isInitialized, setInitialized] = useState<boolean>(false)
  const [isAuthenticated, setAuthenticated] = useState<boolean>(false)

  const initialize = async (retryAttempts: number = 3) => {
    // catch clause variables are `unknown` by default; getErrorMessage
    // accepts `any`, so `unknown` here avoids a new no-explicit-any warning.
    let lastError: unknown
    for (let attempt = 1; attempt <= retryAttempts; attempt++) {
      try {
        const isAuthenticatedResponse = await auth.init()
        setAuthenticated(isAuthenticatedResponse)
        setInitialized(true)
        return
      } catch (error) {
        lastError = error
        console.error(
          `Initialization attempt ${attempt} failed with: ${getErrorMessage(
            error,
          )}`,
        )
        errorHandler(error, errorMapper)
        // Only back off when another attempt is actually coming.
        if (attempt < retryAttempts) {
          await new Promise((resolve) => setTimeout(resolve, attempt * 1000))
        }
      }
    }
    setAuthenticated(false)
    // isInitialized must still flip to true here: it lets PrivateRoute's
    // `if (isInitialized && !isAuthenticated) login()` effect fire and send
    // the user to the Keycloak login page, instead of leaving App.jsx stuck
    // rendering <LoaderOverlay /> forever (perceived as a blank/white screen).
    //
    // CAVEAT: this only escapes when the user has no Keycloak session. If they
    // do, Keycloak returns a code immediately and we land back here, so any
    // failure that survives auth.init()'s own fallback becomes a redirect loop
    // with a callback stacked onto the fragment each time. auth.init() must keep
    // that from happening; this is the last resort, not the safety net.
    setInitialized(true)
    console.error(
      `Initialization failed after ${retryAttempts} attempts. Last error: ${getErrorMessage(
        lastError,
      )}`,
    )
  }

  const login = () => {
    auth.login()
  }

  // WHY useCallback: a new identity every render would re-fire any effect that
  // depends on `logout` (e.g. App.jsx's No Access check) on every render, not
  // just when the condition it guards actually changes.
  const logout = useCallback((redirectUri?: string) => {
    auth.logout(redirectUri)
  }, [])

  useEffect(() => {
    if (!isInitialized) {
      initialize()
    }
  }, [isInitialized])

  return (
    <AuthContext.Provider
      value={{ isAuthenticated, isInitialized, login, logout }}
    >
      {props.children}
    </AuthContext.Provider>
  )
}

export default AuthProvider
